import { useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

// ============================================================================
// RescindApprovalButton — pt.38 (2026-08-26).
// Replaces the earlier window.prompt-based flow. `window.prompt` is
// silently blocked by many browsers/extensions on production custom
// domains (Chrome has heuristics that auto-dismiss prompts after
// user gestures, some ad-blockers strip them entirely). The prompt
// never appeared for Brett on currentcontrolhq.com → the click
// looked dead. This uses a proper shadcn Dialog which is unblockable.
// ============================================================================

export default function RescindApprovalButton({ candidateId, approval, onRescinded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");

  const reasonTrimmedLen = reason.trim().length;
  const canSubmit = reasonTrimmedLen >= 10 && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post(
        `/hiring/candidates/${candidateId}/approvals/${approval.id}/rescind`,
        { reason: reason.trim() },
      );
      toast.success(`Approval rescinded (${approval.type})`);
      setOpen(false);
      setReason("");
      onRescinded?.();
    } catch (e) {
      toast.error(extractApiError(e) || "Rescind failed");
    } finally {
      setSaving(false);
    }
  }

  function handleOpenChange(next) {
    setOpen(next);
    if (next) setReason(""); // reset on open
  }

  const gatedAction = approval.type === "staff_meeting_invite"
    ? "Send staff meeting invite"
    : "Extend offer";

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        className="text-xs text-[#dc2626] hover:underline"
        data-testid={`hiring-timeline-rescind-btn-${approval.id}`}
      >
        Rescind
      </button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid={`hiring-timeline-rescind-modal-${approval.id}`}
        >
          <DialogHeader>
            <DialogTitle>Rescind {approval.type} approval</DialogTitle>
          </DialogHeader>

          <div
            className="flex gap-2 items-start text-xs text-[#78350f] bg-[#fffbeb] border border-[#fbbf24] rounded p-3"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              This re-locks <span className="font-medium">{gatedAction}</span> for
              this candidate. The original approval stays on the timeline
              marked <span className="font-medium">Rescinded</span> for the
              audit trail. Any side-effects that already happened (sent invite,
              extended offer, ownership handoff) stay put — only future gated
              actions re-lock.
            </div>
          </div>

          <div>
            <label
              htmlFor={`rescind-reason-${approval.id}`}
              className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1"
            >
              Reason (≥10 characters, written to audit log)
            </label>
            <Textarea
              id={`rescind-reason-${approval.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. interviewer wants a follow-up first"
              rows={3}
              data-testid={`hiring-timeline-rescind-reason-${approval.id}`}
            />
            <div
              className={`text-xs mt-1 ${
                reasonTrimmedLen >= 10 ? "text-[#166534]" : "text-[#4b5563]"
              }`}
            >
              {reasonTrimmedLen} / 10 minimum
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid={`hiring-timeline-rescind-cancel-${approval.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              data-testid={`hiring-timeline-rescind-submit-${approval.id}`}
            >
              {saving ? "Rescinding…" : "Rescind approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
